import { Messages, PropertyKind, PropertyType } from '@/constants';
import { Icons, FormValidations } from '@/utils';
import { NForm, NFormItem, NSelect, NCard, NH5, NIcon, NSpace, NText } from 'naive-ui';
import { defineComponent, type PropType } from 'vue';
import { defineForm } from './FormBase';

interface PropertyPurposeAndTypeData {
	propertyType?: PropertyType; // Finalidade: Residencial ou Comercial
	propertyKind?: PropertyKind | null; // Tipo: Casa, Apartamento, etc.
}

export interface IPropertyPurposeAndTypeForm {
	validate(): Promise<unknown>;
}

// Mesmo tratamento visual de "desabilitado" usado no AppSelectPropertyType.
const DISABLED_CARD_STYLE = {
	opacity: 0.6,
	cursor: 'not-allowed',
	pointerEvents: 'none',
} as const;

// Schema de validação
const PropertyPurposeAndTypeFormSchema = {
	propertyType: [FormValidations.IsRequired],
	propertyKind: [FormValidations.IsSelectRequired],
};

export const PropertyPurposeAndTypeForm = defineComponent({
	name: 'PropertyPurposeAndTypeForm',
	extends: defineForm<PropertyPurposeAndTypeData>({
		startValue: { propertyType: undefined, propertyKind: undefined } as PropertyPurposeAndTypeData,
		schema: PropertyPurposeAndTypeFormSchema as any,
	}),

	props: {
		// Restringe os tipos de imóvel exibidos (ex.: Seguro Incêndio só mostra os
		// tipos aceitos pela Porto Seguro). undefined = mostra todos.
		allowedKinds: { type: Array as PropType<PropertyKind[]>, default: undefined },

		// Bloqueia a finalidade Residencial (ex.: locatário Pessoa Jurídica só pode
		// contratar garantia para imóvel comercial).
		commercialOnly: { type: Boolean, default: false },
	},

	computed: {
		realEstateTypeOptions() {
			let options: { label: string; value: PropertyKind }[] = [];

			// Se for Residencial, mostra apenas opções residenciais
			if (this.formData.propertyType === PropertyType.Residential) {
				options = [
					{ label: 'Casa', value: PropertyKind.House },
					{ label: 'Apartamento', value: PropertyKind.Apartment },
					{ label: 'Loft', value: PropertyKind.Loft },
					{ label: 'Kitnet', value: PropertyKind.Studio },
					{ label: 'Casa de Madeira', value: PropertyKind.WoodenHouse },
				];
			}
			// Se for Comercial, mostra apenas opções comerciais
			else if (this.formData.propertyType === PropertyType.Commercial) {
				options = [
					{ label: 'Loja Comercial', value: PropertyKind.CommercialStore },
					{ label: 'Sala Comercial', value: PropertyKind.CommercialOffice },
					{ label: 'Galpão', value: PropertyKind.Warehouse },
					{ label: 'Consultórios', value: PropertyKind.Consultorios },
					{ label: 'Escolas', value: PropertyKind.Escolas },
					{ label: 'Escritórios', value: PropertyKind.Escritorios },
					{ label: 'Hospitais', value: PropertyKind.Hospitais },
					{ label: 'Comércio e Serviços', value: PropertyKind.ComercioServicos },
					{ label: 'Indústrias', value: PropertyKind.Industrias },
				];
			}

			if (this.allowedKinds && this.allowedKinds.length) {
				options = options.filter((o) => this.allowedKinds!.includes(o.value));
			}

			return options;
		},

		isPropertyTypeSelected() {
			return this.formData.propertyType !== undefined;
		},
	},

	watch: {
		// Corrige uma finalidade Residencial já selecionada quando a restrição passa a valer:
		// o usuário pode voltar ao passo do locatário e trocar o CPF por um CNPJ, e propostas
		// antigas podem abrir as telas de edição já como Residencial.
		commercialOnly: { immediate: true, handler: 'enforceCommercialOnly' },
		'formData.propertyType': 'enforceCommercialOnly',
	},

	methods: {
		enforceCommercialOnly() {
			if (!this.commercialOnly) return;
			if (this.formData.propertyType !== PropertyType.Residential) return;

			this.formData.propertyType = PropertyType.Commercial;
			// Os tipos residenciais não existem na lista comercial: obriga a escolher de novo.
			this.formData.propertyKind = null;
			this.$emit('update:modelValue', this.formData);
		},

		isDisabled(value: PropertyType) {
			return this.commercialOnly && value === PropertyType.Residential;
		},

		handlePropertyTypeChange(value: PropertyType) {
			if (this.isDisabled(value)) return;

			this.formData.propertyType = value;
			// Limpa o tipo de imóvel quando muda a finalidade
			this.formData.propertyKind = null;
			this.$emit('update:modelValue', this.formData);
		},

		handlePropertyKindChange(value: PropertyKind | null) {
			this.formData.propertyKind = value;
			this.$emit('update:modelValue', this.formData);
		},

		isSelected(value: PropertyType) {
			return this.formData.propertyType == value ? 'app-card-select--is-selected' : '';
		},
	},

	render() {
		const propertyTypeOptions = [
			{ value: PropertyType.Residential, label: 'Residencial', Icon: <NIcon size="48" component={Icons.Home} /> },
			{ value: PropertyType.Commercial, label: 'Comercial', Icon: <NIcon size="48" component={Icons.Commercial} /> },
		];

		return (
			<NForm
				size="large"
				ref="formRef"
				{...{ attrs: this.$attrs }}
				label-placement="top"
				model={this.formData}
				onSubmit={this.handleSubmit}
			>
				<div class="mt-2">
					<NFormItem 
						label="Finalidade da Locação" 
						path="propertyType"
						required
						rule={PropertyPurposeAndTypeFormSchema.propertyType[0]}
					>
						<NSpace vertical>
							<NSpace>
								{propertyTypeOptions.map((option) => (
									<div onClick={() => this.handlePropertyTypeChange(option.value)}>
										<NCard
											hoverable
											data-cy={`property-type-${option.value}`}
											class={[
												'text-align-center app-card-select',
												this.isSelected(option.value),
											]}
											style={this.isDisabled(option.value) ? DISABLED_CARD_STYLE : undefined}
										>
											{option.Icon}
											<NH5 class="m0">{option.label}</NH5>
										</NCard>
									</div>
								))}
							</NSpace>

							{this.commercialOnly && (
								<NText depth="3" data-cy="commercial-only-note">
									{Messages.ERROR_LEGAL_PERSON_REQUIRES_COMMERCIAL_PROPERTY}
								</NText>
							)}
						</NSpace>
					</NFormItem>

					{this.isPropertyTypeSelected && (
						<NFormItem 
							label="Tipo de Imóvel" 
							path="propertyKind"
							required
							rule={PropertyPurposeAndTypeFormSchema.propertyKind[0]}
						>
							<NSelect
								v-model:value={this.formData.propertyKind}
								onUpdateValue={(v) => this.handlePropertyKindChange(v as any)}
								options={this.realEstateTypeOptions}
								placeholder="Selecione o tipo de imóvel"
							/>
						</NFormItem>
					)}
				</div>
			</NForm>
		);
	},
});

export default PropertyPurposeAndTypeForm;

